import { appointmentTypeKey } from '@/utils/appointmentFrequency';

const SELECTABLE_STATUSES = new Set(['scheduled', 'no show', 'pending']);

export const ADD_ENCOUNTER_VALUE = '__add_encounter__';

export type ReportAppointment = {
  id: string;
  status?: string | null;
  scheduledDate?: string | Date | null;
  type?: string | null;
  emrEncounterId?: string | null;
  linkedTransmissionReport?: { id?: number | string | null; signatureStatus?: string | null } | null;
};

export type TransmissionTaskRef = {
  businessState?: string | null;
  appointmentId?: string | null;
  transmissionReportId?: string | null;
  taskType?: { code?: string | null } | null;
};

function localDay(value?: string | Date | null): string | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
    if (match) return match[1];
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function isFutureAppointment(
  scheduledDate?: string | Date | null,
  now: Date = new Date()
): boolean {
  const day = localDay(scheduledDate);
  const today = localDay(now);
  if (!day || !today) return false;
  return day > today;
}

function isOpenTask(state?: string | null): boolean {
  const key = appointmentTypeKey(state);
  return key !== 'canceled' && key !== 'cancelled' && key !== 'completed';
}

function isTransmissionTask(task: TransmissionTaskRef): boolean {
  return task.taskType?.code === 'transmission_tracking';
}

/** The open transmission-tracking task already tied to this report, if any. */
export function ownTransmissionTask(
  tasks: readonly TransmissionTaskRef[],
  reportId: number | string
): TransmissionTaskRef | undefined {
  const reportKey = String(reportId);
  return tasks.find(
    (task) =>
      isTransmissionTask(task) &&
      isOpenTask(task.businessState) &&
      task.transmissionReportId != null &&
      String(task.transmissionReportId) === reportKey
  );
}

/**
 * Another report already has this appointment. A draft link counts the same as a
 * signed one: the appointment list only includes the other report id, and the
 * link API rejects any existing link.
 */
export function isLinkedToAnotherReport(
  appointment: ReportAppointment,
  reportId: number | string
): boolean {
  const linkedId = appointment.linkedTransmissionReport?.id;
  if (linkedId == null || linkedId === '') return false;
  return String(linkedId) !== String(reportId);
}

export function isInClinicAppointment(appointment: { type?: string | null }): boolean {
  return appointmentTypeKey(appointment.type) === 'in clinic';
}

export function isEligibleDraftAppointment(
  appointment: ReportAppointment,
  reportId: number | string,
  now: Date = new Date(),
  inClinicOnly = false
): boolean {
  if (!SELECTABLE_STATUSES.has(appointmentTypeKey(appointment.status))) return false;
  if (isFutureAppointment(appointment.scheduledDate, now)) return false;
  if (isLinkedToAnotherReport(appointment, reportId)) return false;
  if (inClinicOnly && !isInClinicAppointment(appointment)) return false;
  return true;
}

/**
 * One appointment to link when the report has none. A transmission task's
 * appointment wins. Otherwise a draft report is filled only when one visit qualifies.
 */
export function appointmentToPreselect(
  appointments: readonly ReportAppointment[],
  tasks: readonly TransmissionTaskRef[],
  reportId: number | string,
  currentAppointmentId?: string | null,
  now: Date = new Date(),
  inClinicOnly = false
): ReportAppointment | null {
  if (currentAppointmentId) return null;
  const ownTask = ownTransmissionTask(tasks, reportId);
  if (ownTask?.appointmentId) {
    const taskAppointment =
      appointments.find((appointment) => appointment.id === ownTask.appointmentId) ?? null;
    if (inClinicOnly && (!taskAppointment || !isInClinicAppointment(taskAppointment))) return null;
    return taskAppointment;
  }
  if (ownTask) return null;
  const eligible = appointments.filter((appointment) =>
    isEligibleDraftAppointment(appointment, reportId, now, inClinicOnly)
  );
  return eligible.length === 1 ? eligible[0] : null;
}
